import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Combobox,
 ComboboxItem,  ComboboxProps,
  Group,
  Input,
  InputBase,
  InputBaseProps,
  ScrollArea,
  Stack,
  Text,
  useCombobox } from "@mantine/core";
import { IconCheck, IconSearch } from "@tabler/icons-react";
import i18n from "src/i18n";

import { StyledInputBase } from "../ConsultationSelect/ConsultationSelect.styled";


function SelectOption({ label, value }: ComboboxItem) {
  return (
    <Stack gap={5}>
      <Text fz={14}>{label}</Text>
      <Text fz={12} c="dimmed" ff={"var(--font-secondary)"}>
        {value}
      </Text>
    </Stack>
  );
}

interface PatientsSelectProps extends InputBaseProps {
  placeholder?: string;
  label?: string;
  value: string | null;
  setValue: React.Dispatch<React.SetStateAction<string | null>>;
  data: ComboboxItem[] | [];
  disabled?: boolean;
  isLoading?: boolean;
  comboboxDropdownProps?: ComboboxProps;
}

export const PatientsSelect = ({
  placeholder = i18n.t("search-for-patient"),
  label,
  value,
  setValue,
  data,
  disabled,
  isLoading = false,
  comboboxDropdownProps,
  ...rest
}: PatientsSelectProps) => {
  const { t } = useTranslation();
  //   const [value, setValue] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  const combobox = useCombobox({
    onDropdownClose: () => {
      combobox.resetSelectedOption();
      combobox.focusTarget();
      setSearch("");
    },

    onDropdownOpen: () => {
      combobox.focusSearchInput();
    },
  });

  const selectedOption = data?.find(item => item.value === value);

  const options = data
    ?.filter(
      item =>
        item.label.toLowerCase().includes(search.toLowerCase().trim()) ||
        item.value.toLowerCase().includes(search.toLowerCase().trim())
    )
    .map(item => (
      <Combobox.Option
        value={item.value}
        key={item.value}
        active={item.value === value}>
        <Group gap="xs" justify="space-between">
          <SelectOption {...item} />
          {item.value === value && (
            <IconCheck size={16} color={"var(--mantine-color-black)"} />
          )}
        </Group>
      </Combobox.Option>
    ));

  return (
    <Combobox
      store={combobox}
      width={"60%"}
      withinPortal={false}
      onOptionSubmit={val => {
        // console.log(val);
        setValue(val);
        combobox.closeDropdown();
      }}
      {...comboboxDropdownProps}>
      <Combobox.Target>
        <StyledInputBase>
          <InputBase
            component="button"
            type="button"
            disabled={disabled}
            label={label}
            pointer
            onClick={() => combobox.toggleDropdown()}
            multiline
            {...rest}>
            {selectedOption ? (
              <SelectOption {...selectedOption} />
            ) : (
              <Input.Placeholder>{placeholder}</Input.Placeholder>
            )}
          </InputBase>
        </StyledInputBase>
      </Combobox.Target>

      <Combobox.Dropdown>
        <Combobox.Search
          leftSection={<IconSearch size={16} />}
          value={search}
          onChange={event => setSearch(event.currentTarget.value)}
          placeholder={t("search-for-patient")}
        />
        <Combobox.Options>
          <ScrollArea.Autosize mah={200} type="auto" scrollbarSize={7}>
            {options.length > 0 ? (
              options
            ) : (
              <Combobox.Empty>{t("nothing-found")}</Combobox.Empty>
            )}
          </ScrollArea.Autosize>
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
};
